import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, TrendingUp, TrendingDown, Star, AlertCircle,
  CheckCircle, Lightbulb, BarChart3, Loader2, Car, Calendar, Gauge, Fuel, Settings,
  Copy, Check, ExternalLink, Megaphone, FileText, Sparkles
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EstimationResult({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [estimation, setEstimation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState(null);
  const [generatingListing, setGeneratingListing] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [sites, setSites] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [estRes, sitesRes] = await Promise.all([
          axios.get(`${API}/estimations/${id}`, { withCredentials: true }),
          axios.get(`${API}/marketplace-sites`, { withCredentials: true }),
        ]);
        setEstimation(estRes.data);
        setSites(sitesRes.data.sites || []);
        if (estRes.data.listing) {
          setListing(estRes.data.listing);
        }
      } catch (err) {
        console.error("Fetch error:", err);
        navigate("/dashboard", { replace: true });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const formatPrice = (price) => {
    if (!price && price !== 0) return "-";
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(price);
  };

  const generateListing = async () => {
    setGeneratingListing(true);
    try {
      const res = await axios.post(`${API}/estimations/${id}/generate-listing`, {}, { withCredentials: true });
      setListing(res.data.listing);
    } catch (err) {
      console.error("Generate listing error:", err);
    } finally {
      setGeneratingListing(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // Fallback
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const getFullListingText = () => {
    if (!listing || !estimation) return "";
    return `${listing.titre}\n\n${listing.description}\n\nPrix: ${formatPrice(listing.prix_suggere)}\n\nCaracteristiques:\n- Marque: ${estimation.brand}\n- Modele: ${estimation.model}\n- Annee: ${estimation.year}\n- Kilometrage: ${parseInt(estimation.mileage).toLocaleString("fr-FR")} km\n- Carburant: ${estimation.fuel_type}\n- Boite: ${estimation.transmission}\n- Etat: ${estimation.condition}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header user={user} />
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 font-medium">Chargement de l'estimation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!estimation) return null;

  const result = estimation.result;
  const isCompleted = estimation.status === "completed" && result && !result.error;

  return (
    <div className="min-h-screen bg-white" data-testid="estimation-result-page">
      <Header user={user} />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          className="text-slate-600 mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au tableau de bord
        </Button>

        {/* Vehicle Summary */}
        <div className="flex flex-col sm:flex-row items-start gap-6 mb-8 animate-fade-in-up">
          {estimation.photos && estimation.photos.length > 0 ? (
            <img
              src={estimation.photos[0]}
              alt="Vehicule"
              className="w-full sm:w-40 h-28 object-cover rounded-xl"
              data-testid="vehicle-photo"
            />
          ) : (
            <div className="w-full sm:w-40 h-28 bg-slate-100 rounded-xl flex items-center justify-center">
              <Car className="w-8 h-8 text-slate-300" />
            </div>
          )}
          <div className="flex-1">
            <h1
              className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="vehicle-title"
            >
              {estimation.brand} {estimation.model}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500">
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{estimation.year}</span>
              <span className="flex items-center gap-1"><Gauge className="w-3.5 h-3.5" />{parseInt(estimation.mileage).toLocaleString("fr-FR")} km</span>
              <span className="flex items-center gap-1"><Fuel className="w-3.5 h-3.5" />{estimation.fuel_type}</span>
              <span className="flex items-center gap-1"><Settings className="w-3.5 h-3.5" />{estimation.transmission}</span>
            </div>
            <Badge className="mt-3 bg-slate-100 text-slate-700 hover:bg-slate-100">{estimation.condition}</Badge>
          </div>
        </div>

        {estimation.status === "processing" && (
          <div className="text-center py-16 animate-fade-in-up" data-testid="processing-state">
            <div className="relative inline-flex items-center justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center">
                <BarChart3 className="w-8 h-8 text-orange-500" />
              </div>
              <div className="absolute w-16 h-16 rounded-full border-2 border-orange-300 pulse-ring" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Estimation en cours...
            </h2>
            <p className="text-slate-600">L'intelligence artificielle analyse votre vehicule.</p>
          </div>
        )}

        {estimation.status === "error" && (
          <div className="text-center py-16 animate-fade-in-up" data-testid="error-state">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Erreur lors de l'estimation
            </h2>
            <p className="text-slate-600 mb-6">Une erreur est survenue. Veuillez reessayer.</p>
            <Button
              onClick={() => navigate("/new-estimation")}
              className="rounded-lg bg-slate-900 text-white hover:bg-slate-800 font-medium px-8 py-3 h-auto btn-press"
              data-testid="retry-button"
            >
              Nouvelle estimation
            </Button>
          </div>
        )}

        {isCompleted && (
          <Tabs defaultValue="estimation" className="space-y-6">
            <TabsList className="bg-slate-100 p-1 rounded-lg" data-testid="result-tabs">
              <TabsTrigger value="estimation" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm px-6" data-testid="tab-estimation">
                <BarChart3 className="w-4 h-4 mr-2" />
                Estimation
              </TabsTrigger>
              <TabsTrigger value="publish" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm px-6" data-testid="tab-publish">
                <Megaphone className="w-4 h-4 mr-2" />
                Publier l'annonce
              </TabsTrigger>
            </TabsList>

            {/* ====== ESTIMATION TAB ====== */}
            <TabsContent value="estimation" className="space-y-8 animate-fade-in-up" data-testid="estimation-results">
              {/* Main Price Card */}
              <div className="result-card-dark p-8" data-testid="main-price-card">
                <p className="text-sm text-slate-400 uppercase tracking-wider font-medium mb-2">Estimation moyenne</p>
                <p
                  className="text-4xl md:text-5xl font-bold"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  data-testid="average-price"
                >
                  {formatPrice(result.estimation_moyenne)}
                </p>
                {result.cote_confiance && (
                  <div className="mt-4 flex items-center gap-2">
                    <div className="flex gap-0.5">
                      {[...Array(10)].map((_, i) => (
                        <div
                          key={i}
                          className={`w-6 h-1.5 rounded-full ${
                            i < result.cote_confiance ? "bg-orange-500" : "bg-slate-700"
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-slate-400 ml-2">
                      Confiance: {result.cote_confiance}/10
                    </span>
                  </div>
                )}
              </div>

              {/* Marketplace Prices */}
              <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden" data-testid="marketplace-prices">
                <div className="p-6 pb-4">
                  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Prix par marketplace
                  </h3>
                </div>
                {[
                  { key: "estimation_leboncoin", name: "Le Bon Coin", color: "bg-orange-500" },
                  { key: "estimation_lacentrale", name: "La Centrale", color: "bg-blue-500" },
                  { key: "estimation_argus", name: "Argus", color: "bg-emerald-500" },
                ].map((mp) => {
                  const data = result[mp.key];
                  if (!data) return null;
                  return (
                    <div key={mp.key} className="px-6 py-4 border-t border-slate-100" data-testid={`price-${mp.key}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-2.5 h-2.5 rounded-full ${mp.color}`} />
                          <span className="marketplace-tag text-slate-600">{mp.name}</span>
                        </div>
                        <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {formatPrice(data.avg)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-slate-400 ml-5">
                        <span className="flex items-center gap-1">
                          <TrendingDown className="w-3 h-3" />
                          Min: {formatPrice(data.min)}
                        </span>
                        <span className="flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" />
                          Max: {formatPrice(data.max)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Points forts / faibles */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {result.points_forts && result.points_forts.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6" data-testid="points-forts">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-emerald-700 uppercase tracking-wider mb-4">
                      <CheckCircle className="w-4 h-4" />
                      Points forts
                    </h3>
                    <ul className="space-y-2">
                      {result.points_forts.map((p, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <Star className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.points_faibles && result.points_faibles.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6" data-testid="points-faibles">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-orange-700 uppercase tracking-wider mb-4">
                      <AlertCircle className="w-4 h-4" />
                      Points faibles
                    </h3>
                    <ul className="space-y-2">
                      {result.points_faibles.map((p, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <AlertCircle className="w-3.5 h-3.5 text-orange-500 mt-0.5 flex-shrink-0" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Conseils et analyse */}
              {(result.conseils_vente || result.analyse_photos || result.tendance_marche || result.details_estimation) && (
                <div className="bg-slate-50 rounded-xl border border-slate-100 p-6 space-y-4" data-testid="analysis-details">
                  {result.conseils_vente && (
                    <div>
                      <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-2">
                        <Lightbulb className="w-4 h-4 text-orange-500" />
                        Conseils de vente
                      </h4>
                      <p className="text-sm text-slate-600 leading-relaxed">{result.conseils_vente}</p>
                    </div>
                  )}
                  {result.analyse_photos && (
                    <div>
                      <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-2">
                        <BarChart3 className="w-4 h-4 text-blue-500" />
                        Analyse des photos
                      </h4>
                      <p className="text-sm text-slate-600 leading-relaxed">{result.analyse_photos}</p>
                    </div>
                  )}
                  {result.tendance_marche && (
                    <div>
                      <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-2">
                        <TrendingUp className="w-4 h-4 text-emerald-500" />
                        Tendance du marche
                      </h4>
                      <p className="text-sm text-slate-600 leading-relaxed">{result.tendance_marche}</p>
                    </div>
                  )}
                  {result.details_estimation && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 mb-2">Details de l'estimation</h4>
                      <p className="text-sm text-slate-600 leading-relaxed">{result.details_estimation}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Photo gallery */}
              {estimation.photos && estimation.photos.length > 1 && (
                <div data-testid="photo-gallery">
                  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Photos du vehicule
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {estimation.photos.map((photo, i) => (
                      <img
                        key={i}
                        src={photo}
                        alt={`Photo ${i + 1}`}
                        className="w-full aspect-[4/3] object-cover rounded-xl photo-thumb"
                      />
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* ====== PUBLISH TAB ====== */}
            <TabsContent value="publish" className="space-y-8 animate-fade-in-up" data-testid="publish-section">
              
              {/* Generate listing CTA */}
              {!listing && !generatingListing && (
                <div className="text-center py-12 bg-slate-50 rounded-2xl border border-slate-100" data-testid="generate-listing-cta">
                  <div className="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center mx-auto mb-5">
                    <Sparkles className="w-8 h-8 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Generez votre annonce
                  </h3>
                  <p className="text-slate-600 mb-6 max-w-md mx-auto text-sm">
                    L'IA va creer une annonce optimisee pour maximiser vos chances de vente sur toutes les plateformes.
                  </p>
                  <Button
                    onClick={generateListing}
                    className="rounded-lg bg-slate-900 text-white hover:bg-slate-800 font-medium px-8 py-3 h-auto btn-press"
                    data-testid="generate-listing-button"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generer l'annonce par IA
                  </Button>
                </div>
              )}

              {/* Generating state */}
              {generatingListing && (
                <div className="text-center py-12 bg-slate-50 rounded-2xl border border-slate-100" data-testid="generating-listing">
                  <Loader2 className="w-10 h-10 animate-spin text-orange-500 mx-auto mb-4" />
                  <p className="text-slate-600 font-medium">Generation de l'annonce en cours...</p>
                  <p className="text-sm text-slate-400 mt-1">L'IA redige votre annonce optimisee</p>
                </div>
              )}

              {/* Listing content */}
              {listing && (
                <>
                  {/* Listing preview card */}
                  <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden" data-testid="listing-preview">
                    <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900 uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <FileText className="w-4 h-4" />
                        Votre annonce
                      </h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(getFullListingText(), "full")}
                        className="text-slate-500 hover:text-slate-900"
                        data-testid="copy-full-listing"
                      >
                        {copiedField === "full" ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                        <span className="ml-1 text-xs">{copiedField === "full" ? "Copie !" : "Tout copier"}</span>
                      </Button>
                    </div>
                    
                    <div className="p-6 space-y-4">
                      {/* Title */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Titre</span>
                          <button
                            onClick={() => copyToClipboard(listing.titre, "titre")}
                            className="text-slate-400 hover:text-slate-700 transition-colors"
                            data-testid="copy-title"
                          >
                            {copiedField === "titre" ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                        <p className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {listing.titre}
                        </p>
                      </div>

                      {/* Price */}
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg">
                        <span className="text-sm font-medium">Prix suggere:</span>
                        <span className="text-lg font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="listing-price">
                          {formatPrice(listing.prix_suggere)}
                        </span>
                      </div>

                      {/* Description */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Description</span>
                          <button
                            onClick={() => copyToClipboard(listing.description, "desc")}
                            className="text-slate-400 hover:text-slate-700 transition-colors"
                            data-testid="copy-description"
                          >
                            {copiedField === "desc" ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700 leading-relaxed whitespace-pre-line" data-testid="listing-description">
                          {listing.description}
                        </div>
                      </div>

                      {/* Tags */}
                      {listing.tags && listing.tags.length > 0 && (
                        <div>
                          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider block mb-2">Mots-cles</span>
                          <div className="flex flex-wrap gap-2" data-testid="listing-tags">
                            {listing.tags.map((tag, i) => (
                              <span
                                key={i}
                                className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Conseils */}
                      {listing.conseils_publication && (
                        <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-100">
                          <h4 className="flex items-center gap-2 text-sm font-semibold text-orange-800 mb-2">
                            <Lightbulb className="w-4 h-4" />
                            Conseils de publication
                          </h4>
                          <p className="text-sm text-orange-700 leading-relaxed">{listing.conseils_publication}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Photos reminder */}
                  {estimation.photos && estimation.photos.length > 0 && (
                    <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6" data-testid="photos-for-listing">
                      <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Photos a joindre a l'annonce
                      </h3>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                        {estimation.photos.map((photo, i) => (
                          <img
                            key={i}
                            src={photo}
                            alt={`Photo ${i + 1}`}
                            className="w-full aspect-square object-cover rounded-lg"
                          />
                        ))}
                      </div>
                      <p className="text-xs text-slate-400 mt-3">Telechargez vos photos pour les ajouter sur chaque site</p>
                    </div>
                  )}

                  {/* Marketplace links */}
                  <div data-testid="marketplace-links">
                    <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Publier sur les sites d'annonces
                    </h3>
                    <p className="text-sm text-slate-500 mb-6">
                      Cliquez sur un site, puis collez votre annonce deja copiee. L'IA a optimise le texte pour chaque plateforme.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {sites.map((site) => (
                        <a
                          key={site.id}
                          href={site.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-200 group"
                          data-testid={`publish-link-${site.id}`}
                        >
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: site.color + '15' }}
                          >
                            <Megaphone className="w-5 h-5" style={{ color: site.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-900 group-hover:text-slate-700">{site.name}</p>
                            <p className="text-xs text-slate-400 truncate">{site.description}</p>
                          </div>
                          <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-slate-500 flex-shrink-0 transition-colors" />
                        </a>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-8">
          <Button
            onClick={() => navigate("/new-estimation")}
            className="rounded-lg bg-slate-900 text-white hover:bg-slate-800 font-medium px-8 py-3 h-auto btn-press"
            data-testid="new-estimation-from-result"
          >
            Nouvelle estimation
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard")}
            className="rounded-lg border-slate-200 text-slate-700 font-medium px-8 py-3 h-auto"
            data-testid="back-dashboard-from-result"
          >
            Tableau de bord
          </Button>
        </div>
      </main>
    </div>
  );
}
